<script setup lang="ts">
import type { BlogCategory, BlogMenuItem } from '~/composables/useBlogApi'

const props = withDefaults(defineProps<{
  menu: BlogMenuItem[]
  categories: BlogCategory[]
  logo?: string
  siteTitle: string
  siteSubtitle?: string
  showDivider?: boolean
  searchTo?: string
}>(), {
  logo: '',
  siteSubtitle: '记录技术沉淀与实践',
  showDivider: false,
  searchTo: ''
})

const emit = defineEmits<{ openSearch: [] }>()
const route = useRoute()
const mobileMenuOpen = ref(false)
const activeCategorySlug = computed(() => String(route.query.category || '').trim())

const categoryHref = (category: BlogCategory) => {
  if (category.type && category.type !== 'menu' && category.path) return category.path
  const slug = category.slug
  if (slug === 'nav') return '/nav'
  if (slug === 'knowledge') return '/knowledges'
  if (slug === 'news') return '/news'
  return `/?category=${encodeURIComponent(slug)}`
}
const menuHref = (item: BlogMenuItem) => item.href
const isExternalURL = (href: string) => /^https?:\/\//i.test(String(href || '').trim())
const publicMenu = computed(() => {
  const items = props.menu
    .filter((item) => !['知识库', '专题', '新闻', '导航', '归档'].includes(item.name) && !['knowledge', 'news'].includes(String(item.slug || '')) && !['/knowledges', '/news', '/nav', '/archives'].includes(item.href))
  // These routes are product-level entry points. They must not disappear when
  // an administrator changes category/page navigation records.
  const fixed: BlogMenuItem[] = [
    { type: 'custom', name: '知识库', href: '/knowledges', sort_order: -4 },
    { type: 'custom', name: '新闻', href: '/news', sort_order: -3 },
    { type: 'custom', name: '导航', href: '/nav', sort_order: -2 },
    { type: 'custom', name: '归档', href: '/archives', sort_order: -1 }
  ]
  return [...fixed, ...items]
})
const unlistedCategories = computed(() => {
  const menuTargets = new Set(publicMenu.value.map(menuHref))
  return props.categories.filter((category) => !menuTargets.has(categoryHref(category)))
})
const isActive = (item: BlogMenuItem) => {
  const href = menuHref(item)
  return (item.type === 'category' && item.slug === activeCategorySlug.value) || href === route.path
}
</script>

<template>
  <header class="home-header sticky top-0 z-30 border-b backdrop-blur-xl transition-[border-color] duration-300" :class="showDivider ? 'border-[#25324a]/70' : 'border-transparent'">
    <div class="mx-auto flex h-16 w-full max-w-[1280px] items-center justify-between gap-2 px-4 sm:gap-4 sm:px-6 lg:px-8 xl:px-0">
      <NuxtLink to="/" class="flex min-w-0 flex-1 items-center gap-2.5 leading-none text-[#e2e8f0]">
        <img v-if="logo" :src="logo" alt="网站 Logo" width="36" height="36" referrerpolicy="no-referrer" class="site-nav-logo h-9 w-9 shrink-0 rounded-lg object-contain" />
        <span class="flex min-w-0 flex-col justify-center gap-0.5">
          <span class="truncate font-title text-[21px] font-bold tracking-[0.3px]">{{ siteTitle }}</span>
          <span class="truncate text-[10px] font-normal tracking-[0.1em] text-[#94a3b8]">{{ siteSubtitle }}</span>
        </span>
      </NuxtLink>

      <div class="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
        <nav aria-label="主导航" class="hidden items-center gap-1.5 text-[13px] font-medium text-[#c7d2e4] md:flex">
          <NuxtLink to="/" class="nav-link px-2.5" :class="route.path === '/' && !activeCategorySlug ? 'nav-link-active' : ''">首页</NuxtLink>
          <NuxtLink
            v-for="item in publicMenu"
            :key="`${item.name}:${menuHref(item)}`"
            :to="menuHref(item)"
            :target="isExternalURL(menuHref(item)) ? '_blank' : undefined"
            :rel="isExternalURL(menuHref(item)) ? 'noopener noreferrer' : undefined"
            class="nav-link px-2.5"
            :class="isActive(item) ? 'nav-link-active' : ''"
          >{{ item.name }}</NuxtLink>
        </nav>
        <div class="flex items-center gap-1">
          <NuxtLink v-if="searchTo" :to="searchTo" class="grid size-9 cursor-pointer place-items-center rounded-full text-[#9fb1cc] transition-colors hover:bg-[#22d3ee]/14 hover:text-[#67e8f9]" aria-label="搜索文章"><AppIcon name="search" class="size-[18px]" /></NuxtLink>
          <button v-else type="button" class="grid size-9 cursor-pointer place-items-center rounded-full text-[#9fb1cc] transition-colors hover:bg-[#22d3ee]/14 hover:text-[#67e8f9]" aria-label="搜索文章" @click="emit('openSearch')"><AppIcon name="search" class="size-[18px]" /></button>
          <PublicThemeToggle />
          <button type="button" class="grid size-9 cursor-pointer place-items-center rounded-full text-[#9fb1cc] transition-colors hover:bg-[#22d3ee]/14 hover:text-[#67e8f9] md:hidden" :aria-label="mobileMenuOpen ? '关闭菜单' : '打开菜单'" @click="mobileMenuOpen = !mobileMenuOpen"><AppIcon :name="mobileMenuOpen ? 'x' : 'menu'" class="size-5" /></button>
          <PublicMemberMenu />
        </div>
      </div>
    </div>
    <div v-if="mobileMenuOpen" class="mobile-nav-panel md:hidden">
      <NuxtLink to="/" @click="mobileMenuOpen = false">首页</NuxtLink>
      <NuxtLink
        v-for="item in publicMenu"
        :key="`${item.name}:${menuHref(item)}`"
        :to="menuHref(item)"
        :target="isExternalURL(menuHref(item)) ? '_blank' : undefined"
        :rel="isExternalURL(menuHref(item)) ? 'noopener noreferrer' : undefined"
        @click="mobileMenuOpen = false"
      >{{ item.name }}</NuxtLink>
      <div v-if="unlistedCategories.length" class="mobile-nav-categories">
        <NuxtLink
          v-for="category in unlistedCategories"
          :key="category.id"
          :to="categoryHref(category)"
          :target="isExternalURL(categoryHref(category)) ? '_blank' : undefined"
          :rel="isExternalURL(categoryHref(category)) ? 'noopener noreferrer' : undefined"
          @click="mobileMenuOpen = false"
        >{{ category.name }}</NuxtLink>
      </div>
    </div>
  </header>
</template>
